<template>
  <div class="billings-list flex flex-col">
    <!-- 日期範圍選擇器 - 參考 OrderPayoutsList 的佈局 -->
    <div class="flex justify-between items-center my-4">
      <date-range-picker
        v-model="state.dateRange"
        :placeholder="$t('profiles.payout_item_search_form.select_date_range')"
        ref="calendarRef"
        calendar-id="billing-calendar"
        @visibility-change="isCalendarVisible = $event"
      />
    </div>

    <!-- loading 狀態 -->
    <div v-if="state.initialLoading" class="flex justify-center items-center py-8 mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
      <p class="text-16px font-medium text-center">
        <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
      </p>
    </div>

    <!-- 無資料狀態 -->
    <div v-else-if="billingData.items.length === 0" class="flex-1 flex flex-col items-center justify-center py-8 mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
      <img src="../../images/credit_card_billings.svg" class="block w-12 h-12 mb-4" />
      <p class="text-16px font-medium text-center">
        {{ $t('billings.billing_list.no_billings_found') }}
      </p>
    </div>

    <!-- 有資料時顯示內容 -->
    <div v-else>
      <!-- Desktop Version -->
      <div class="hidden sm:flex flex-col" data-testid="billing-list">
        <div class="grid grid-cols-6 text-grey-3 text-12px font-medium" data-testid="billing-items-header">
          <div class="date">{{ $t('billings.billing_list.date') }}</div>
          <div class="content col-span-3">{{ $t('billings.billing_list.content') }}</div>
          <div class="total">{{ $t('billings.billing_list.total') }}</div>
          <div class="receipt text-center">{{ $t('billings.billing_list.receipt') }}</div>
          <hr class="mt-1 col-span-6 border-gray-500">
        </div>

        <div 
          v-for="billingItem in billingData.items" 
          :key="billingItem.id"
          class="grid grid-cols-6 py-4 items-center bg-gray-600 mt-2 rounded-lg text-14px" 
          data-testid="billing-items-item"
        >
          <div class="date pl-2">{{ billingItem.created_at }}</div>
          <div class="content col-span-3">
            <div class="list-decimal text-left">
              <p 
                v-for="orderItem in billingItem.order_items" 
                :key="`${billingItem.id}-${orderItem.post_title}`"
                class="truncate"
              >
                {{ orderItem.post_title }} - {{ orderItem.variant_name }}
              </p>
            </div>
          </div>
          <div class="total">{{ formatCurrency(billingItem.total_price) }}</div>
          <div class="receipt flex justify-center items-center">
            <a 
              :href="billingItem.download_url" 
              class="w-8 h-8 flex justify-center overflow-hidden"
            >
              <img 
                src="../../images/icons/download2.svg" 
                class="w-6 h-6 my-0.5" 
                alt="Download"
              >
            </a>
          </div>
        </div>
      </div>

      <!-- Mobile Version -->
      <div class="sm:hidden flex flex-col" data-testid="mobile-billing-list">
        <div class="grid grid-cols-6 text-grey-3 text-12px font-medium" data-testid="mobile-billing-items-header">
          <div class="date content col-span-4" >{{ $t('billings.mobile_billing_list.date') }} / {{ $t('billings.mobile_billing_list.content') }}</div>
          <div class="total text-center">{{ $t('billings.mobile_billing_list.total') }}</div>
          <div class="receipt text-center">{{ $t('billings.mobile_billing_list.receipt') }}</div>
          <hr class="mt-1 col-span-6 border-gray-500">
        </div>

        <div 
          v-for="billingItem in billingData.items" 
          :key="`mobile-${billingItem.id}`"
          class="grid grid-cols-6 py-4 text-center items-center bg-gray-600 mt-2 rounded-lg text-14px" 
          data-testid="mobile-billing-item"
        >
          <div class="date col-span-6 sm:col-span-1 text-left pl-2">{{ billingItem.created_at }}</div>
          <div class="content col-span-4 pl-2 sm:col-span-3 sm:pl-0">
            <div class="list-decimal text-left">
              <p 
                v-for="orderItem in billingItem.order_items" 
                :key="`mobile-${billingItem.id}-${orderItem.post_title}`"
                class="truncate"
              >
                {{ orderItem.post_title }} - {{ orderItem.variant_name }}
              </p>
            </div>
          </div>
          <div class="total">{{ formatCurrency(billingItem.total_price) }}</div>
          <div class="receipt flex justify-center items-center">
            <a 
              :href="billingItem.download_url" 
              class="w-8 h-8 flex justify-center overflow-hidden"
            >
              <img 
                src="../../images/icons/download2.svg" 
                class="w-6 h-6 my-0.5" 
                alt="Download"
              >
            </a>
          </div>
        </div>
      </div>

      <!-- loading more -->
      <div v-if="state.loading" class="py-4 flex justify-center">
        <p class="text-16px font-medium text-center">
          <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import debounce from 'lodash/debounce'

export default defineComponent({
  name: 'BillingsList',

  setup() {
    const scrollListener = ref(null)
    const initialDataLoaded = ref(false)
    const calendarRef = ref(null)
    const isCalendarVisible = ref(false)

    const state = reactive({
      loading: false,
      initialLoading: true,
      itemsCount: 10,
      dateRange: { start: '', end: '' }
    })

    const billingData = reactive({
      items: [],
      totalCount: 0,
      page: 0,
      hasMore: true
    })

    watch(() => [state.dateRange.start, state.dateRange.end], 
      debounce(([start, end]) => {
        if (initialDataLoaded.value) {
          onSearch()
        }
      }, 300), 
      { deep: true }
    )

    const getQueryParams = () => {
      const params = new URLSearchParams()
      if (state.dateRange.start) params.append('date_start', state.dateRange.start)
      if (state.dateRange.end) params.append('date_end', state.dateRange.end)
      return params
    }

    // 取得賬單資料
    const fetchBillingItems = async (page = 1, perPage = 10) => {
      if (!billingData.hasMore || state.loading) return

      state.loading = true
      try {
        const params = new URLSearchParams({
          page: page,
          per_page: perPage,
          ...Object.fromEntries(getQueryParams())
        })

        const api_path = `/api/v1/profile/billings?${params.toString()}`
        
        const response = await fetch(api_path, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin'
        })
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const data = await response.json()
        const newItems = data.billing_items || []
        
        // 新增項目到現有列表
        newItems.forEach(item => {
          if (!billingData.items.some(existing => existing.id === item.id)) {
            billingData.items.push(item)
          }
        })
        
        // 更新分頁信息
        if (data.pagination) {
          billingData.page = data.pagination.current_page
          billingData.totalCount = data.pagination.total_count
          billingData.hasMore = data.pagination.current_page < data.pagination.total_pages
        }

        // 如果新加載的項目少於請求數量且還有更多，繼續加載
        if (newItems.length < perPage && billingData.hasMore) {
          await fetchBillingItems(billingData.page + 1, perPage)
        } else {
          setTimeout(() => {
            checkAndLoadMore(page, perPage)
          }, 100)
        }

      } catch (error) {
        console.error('Error fetching billing items:', error)
        billingData.hasMore = false
      } finally {
        state.loading = false
        state.initialLoading = false
      }
    }

    const checkAndLoadMore = (page, perPage) => {
      if (!billingData.hasMore || state.loading) return
      
      const documentHeight = document.documentElement.scrollHeight
      const windowHeight = window.innerHeight
      
      if (documentHeight <= windowHeight) {
        fetchBillingItems(billingData.page + 1, perPage)
      }
    }

    const resetDataForNewQuery = () => {
      billingData.items = []
      billingData.totalCount = 0
      billingData.page = 0
      billingData.hasMore = true
    }

    const onSearch = async () => {
      const { start, end } = state.dateRange
      
      // 更新 URL 參數
      const url = new URL(window.location)
      if (start) {
        url.searchParams.set('date_start', start)
      } else {
        url.searchParams.delete('date_start')
      }
      if (end) {
        url.searchParams.set('date_end', end)
      } else {
        url.searchParams.delete('date_end')
      }
      history.pushState({}, '', url)
      
      resetDataForNewQuery()
      scrollToTop()
      await fetchBillingItems(1, state.itemsCount)
    }

    const handleScroll = () => {
      // 隱藏日曆
      if (calendarRef.value && isCalendarVisible.value) {
        calendarRef.value.hide()
      }
      
      debouncedLoadMore()
    }

    const debouncedLoadMore = debounce(() => {
      if (!billingData.hasMore) return
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight
      const scrolledPercentage = (scrollTop / (documentHeight - windowHeight)) * 100
      
      if (scrolledPercentage > 30 && !state.loading) {
        fetchBillingItems(billingData.page + 1, state.itemsCount)
      }
    }, 100)

    const addScrollListener = () => {
      removeScrollListener()
      scrollListener.value = handleScroll
      window.addEventListener('scroll', scrollListener.value, { passive: true })
    }
    
    const removeScrollListener = () => {
      if (scrollListener.value) {
        window.removeEventListener('scroll', scrollListener.value)
        scrollListener.value = null
      }
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount)
    }

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }

    onMounted(() => {
      const urlParams = new URLSearchParams(window.location.search)
      const urlDateStart = urlParams.get('date_start') || ''
      const urlDateEnd = urlParams.get('date_end') || ''
      
      state.dateRange.start = urlDateStart
      state.dateRange.end = urlDateEnd

      fetchBillingItems(1, state.itemsCount)
      addScrollListener()
      initialDataLoaded.value = true
    })

    onUnmounted(() => {
      removeScrollListener()
    })

    return {
      state,
      billingData,
      calendarRef,
      isCalendarVisible,
      formatCurrency,
      onSearch
    }
  }
})
</script>



